import { useFormik } from 'formik';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import * as yup from 'yup';
import { NAME_REGEX } from '@/modules/common/utils/regex.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useCreateInvestorEditMutation,
  useIssuerGetInvestorEditByIdQuery,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { TextInputField } from 'ui';
import { CreateEntityRequest, UserDetailsData } from 'api';

const validationSchema = yup.object<UserDetailsData>({
  first_name: yup
    .string()
    .trim()
    .min(2, 'Must be at least 2 characters')
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .required('This field is required'),
  last_name: yup
    .string()
    .trim()
    .min(3, 'Must be at least 3 characters')
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .required('This field is required'),
  email: yup.string().trim().email('Must be a valid email').required('This field is required'),
});

export const HolderDetails = ({ moveForward }: OnboardingStepProps) => {
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  const { id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(+id, id === 0 || isNaN(+id));
  const { mutateAsync: createInvestor, isLoading } = useCreateInvestorEditMutation({
    onSuccess: data => {
      params.set('step', '1');
      setParams(params, {
        state,
        replace: false,
      });
      navigate(
        {
          pathname: issuerDashboardPaths.dynamic.updateHolder(String(data.data.id)),
          search: params.toString(),
        },
        {
          replace: false,
          state,
        },
      );
    },
  });

  const formik = useFormik<UserDetailsData>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
    },
    enableReinitialize: true,
    onSubmit: async user_data => {
      if (formik.dirty) {
        await createInvestor({
          user_data,
          entity_data: {
            notifications_enabled: true,
            send_information_about_other_investments: true,
            individual_entity_details: {
              country: 'AUS',
            },
            // country: CountryCodeEnum.AUS,
            apply_register_id: Number(registerId),
          } as CreateEntityRequest,
        });
      }
      // moveForward && moveForward();
    },
  });

  useEffect(() => {
    if (!!dataHolder?.data?.new_data?.user_data || !!dataHolder?.data?.new_data?.shareholder_id) {
      moveForward && moveForward();
    }
  }, [dataHolder, moveForward]);

  return (
    <OnboardingHolderWrapper
      title="New holder"
      onClickContinue={formik.handleSubmit}
      formik={formik}
      isLoading={isLoading}
    >
      <div className="flex flex-col sm:gap-8 gap-4">
        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
          <TextInputField label="First name" name={`first_name`} />
          <TextInputField label="Last name" name={`last_name`} />
        </div>
        <TextInputField name="email" label="Email address" placeholder="Email" />
      </div>
    </OnboardingHolderWrapper>
  );
};
